import { useState } from "react";

import type { Addendum, UUID } from "@/core/types";
import { Button2 } from "@/ui2/atoms/Button";
import CheckmarkIcon from "@/assets/images/icons2/icon--checkmark.svg?component";
import CloseIcon from "@/assets/images/icons2/icon--close.svg?component";
import { CosignatureDocumentTypeEnum } from "@/core/constants";
import { CosignNoteModal } from "@/features/cosignature/components/CosignNoteModal";
import { DashboardCard2 } from "@/components/layout/DashboardCard2";
import { Divider2 } from "@/ui2/atoms/Divider";
import { RejectNoteModal } from "@/features/cosignature/components/RejectNoteModal";
import { useCosignSupervisor } from "@/features/cosignature/hooks/useCosignSupervisor";

import { useNoteAddendumCosignDetails } from "../../api/queries/useNoteAddendumCosignDetails";

type AddendumCosignatureReview = {
  addendum: Addendum;
  patientId: UUID;
};

export function AddendumCosignatureReview({ addendum, patientId }: AddendumCosignatureReview) {
  const {
    addendum_id: addendumId,
    is_cosign_required: isCosignRequired,
    signature: providerName,
  } = addendum;
  const { isSupervisor } = useCosignSupervisor();
  const { data: cosignDetails } = useNoteAddendumCosignDetails({
    addendumId,
    enabled: isSupervisor && !!isCosignRequired,
  });

  const [isCosignModalOpen, setIsCosignModalOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  // hide section immediately on success without waiting for cache invalidation to resolve
  const [isSubmitted, setIsSubmitted] = useState(false);

  const isActionable = ["pending", "resubmitted"].includes(cosignDetails?.cosignature_status);

  if (!isSupervisor || !isCosignRequired || !isActionable || isSubmitted) return null;

  return (
    <>
      <Divider2 />

      <DashboardCard2
        className="tw:bg-transparent tw:shadow-none"
        classNameChildren="tw:flex tw:flex-col tw:items-center tw:gap-3"
      >
        <p className="body-md tw:text-center">
          Review the addendum above and approve to co-sign, or reject to return to the author.
        </p>

        <div className="tw:flex tw:justify-center tw:gap-4">
          <Button2
            icon={<CloseIcon />}
            onPress={() => setIsRejectModalOpen(true)}
            variant="destructive-primary"
          >
            Reject Addendum
          </Button2>

          <Button2
            icon={<CheckmarkIcon />}
            onPress={() => setIsCosignModalOpen(true)}
            variant="primary"
          >
            Approve &amp; Co-Sign
          </Button2>
        </div>
      </DashboardCard2>

      <CosignNoteModal
        documentId={addendumId}
        documentType={CosignatureDocumentTypeEnum.note_addendum}
        isOpen={isCosignModalOpen}
        onClose={() => setIsCosignModalOpen(false)}
        onSuccess={() => setIsSubmitted(true)}
        patientId={patientId}
        providerName={providerName}
      />

      <RejectNoteModal
        documentId={addendumId}
        documentType={CosignatureDocumentTypeEnum.note_addendum}
        isOpen={isRejectModalOpen}
        onClose={() => setIsRejectModalOpen(false)}
        onSuccess={() => setIsSubmitted(true)}
        patientId={patientId}
        providerName={providerName}
      />
    </>
  );
}
